import PropTypes from 'prop-types';
import React from 'react';
import { formatDateTime } from '../../../library/utilities/intl.js';
import SmallSection from '../../design/small-section.jsx';
import { DocumentsDownloader } from '../../form/index.js';

const FilePropType = PropTypes.shape({
    url: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    comment: PropTypes.string,
    uploaded_by: PropTypes.string,
    uploaded_time: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
})

const FilesPropType = PropTypes.oneOfType([
    PropTypes.arrayOf(FilePropType),
    FilePropType,
    PropTypes.string,
])

const SECTION_HEADER_STYLE = {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
}

const SECTION_GRID_STYLE = {
    display: 'grid',
    gap: '1rem',
    gridTemplateColumns: '1fr 1fr',
}

const FILE_CONTAINER_STYLE = {
    background: '#ebf2fc',
    padding: '1rem',
}

const ORDER_DOCUMENT_TYPES = {
    vendor_template: "Template Received from Vendor",
    leoprinting_uploaded_sample: "Template Sent to Customer",
    client_artwork: "Artwork Received",
    vendor_proof: "Proof Received from Vendor",
    studio_template: "Template Received from Studio",
    studio_artwork: "Artwork Received from Studio",
    studio_proof: "Proof Received from Studio",
    order_template_to_studio: "Template Sent to Studio",
    order_template_to_client: "Template Sent to Customer",
    order_artwork_to_studio: "Artwork Sent to Studio",
    order_artwork_to_vendor: "Artwork Sent to Vendor",
    order_proof_to_studio: "Proof Sent to Studio",
    order_proof_to_client: "Proof Sent to Customer",
    // order_uploaded_proof_of_delivery_for_files: "Bulk Delivery proof",
    make_artwork_send_to_studio: "Artwork Create Request Sent to Studio",
    make_artwork_from_studio: "Artwork Created by Studio",
    make_artwork_send_to_client: "Created artwork Sent to Customer",
    client_uploaded_address: "Bulk Delivery Addresses",
    dispatched_date_file: "Bulk Delivery Dispatch Dates",
    add_shipping_details_file: "Bulk Delivery Shipping Details",
    // shipping_details_file_confirm_by_order: "Bulk Delivery proof ",
    supplier_files: "Files Sent to Supplier",
    supplier_delivery_proof: "Supplier Delivery Proof",
    supplier_delivery_proof_via_vendor: "Delivery Proof uploaded by Supplier",
    vendor_purchase_order: "Vendor/Finisher Purchase Order",
    vendor_delivery_notes: "Vendor/Finisher Delivery Note",
    supplier_purchase_order: "Supplier Purchase Order",
    supplier_delivery_notes: "Supplier Delivery Note",
    supplier_delivey_notes: "Supplier Delivery Note",
    confirm_proof_by_client: "Proof Approved by Customer",
}

const LEGACY_DOCUMENT_SECTIONS = [
    { key: 'supplier_files', title: 'Files Sent to Vendor' },
    { key: 'vendor_purchase_order', title: ({ finisherTimelineVisible }) => `${finisherTimelineVisible ? 'Finisher' : 'Vendor'} Purchase Order` },
    { key: 'finisher_purchase_order', title: 'Supplier Purchase Order' },
    { key: 'vendor_template', title: 'Vendor Template' },
    { key: 'order_template_to_studio', title: 'Template Sent to Studio' },
    { key: 'studio_template', title: 'Template Received from Studio' },
    { key: 'order_template_to_client', title: 'Template Sent to Customer' },
    { key: 'make_artwork_send_to_studio', title: 'Artwork Create Request to Studio' },
    { key: 'make_artwork_from_studio', title: 'Artwork Created by Studio' },
    { key: 'make_artwork_send_to_client', title: 'Created Artwork Sent to Customer' },
    { key: 'client_artwork', title: 'Customer Artwork' },
    { key: 'order_artwork_to_studio', title: 'Artwork Sent to Studio' },
    { key: 'studio_artwork', title: 'Artwork Received from Studio' },
    { key: 'order_artwork_to_vendor', title: 'Artwork Sent to Vendor' },
    { key: 'vendor_proof', title: 'Proof Received from Vendor' },
    { key: 'order_proof_to_studio', title: 'Proof Sent to Studio' },
    { key: 'studio_proof', title: 'Proof Received from Studio' },
    { key: 'order_proof_to_client', title: 'Proof Sent to Customer' },
    { key: 'vendor_delivery_notes', title: ({ finisherTimelineVisible }) => `${finisherTimelineVisible ? 'Finisher' : 'Vendor'} Delivery Note` },
    { key: 'finisher_delivery_notes', fallbackKeys: ['supplier_delivery_notes', 'supplier_delivey_notes'], title: 'Supplier Delivery Note' },
]

const getFileName = (url) => url?.split('/').pop() || url

const normalizeFiles = (files) => {
    if (!files) {
        return []
    }

    if (Array.isArray(files)) {
        return files.flatMap(normalizeFiles)
    }

    if (typeof files === 'string') {
        const trimmedFiles = files.trim()
        if (!trimmedFiles) {
            return []
        }

        try {
            return normalizeFiles(JSON.parse(trimmedFiles))
        }
        catch {
            return [{ url: trimmedFiles, name: getFileName(trimmedFiles) }]
        }
    }

    if (typeof files === 'object' && files.url) {
        return [{
            ...files,
            name: files.name || getFileName(files.url),
        }]
    }

    return []
}

const getSectionTitle = (title, options) => typeof title === 'function' ? title(options) : title

const getSectionFiles = (documents, key, fallbackKeys = []) => {
    const keys = [key, ...fallbackKeys]
    return keys.reduce((files, currentKey) => files || documents?.[currentKey], null)
}

const OrderDocumentSection = ({ projectId, title, files, dateTime, comment }) => {
    const normalizedFiles = normalizeFiles(files).filter(file => !!file.url)
    const normalizedComment = typeof comment === 'string' ? comment.trim() : ''

    if (normalizedFiles.length === 0) {
        return null
    }

    return (
        <SmallSection header={(<div style={SECTION_HEADER_STYLE}>
            <h3>{title}</h3>
            {!!dateTime && <span style={{ color: '#3a3a3a' }}>{formatDateTime(dateTime)}</span>}
        </div>)}>
            <div style={SECTION_GRID_STYLE}>
                {normalizedFiles.map(file => (
                    <DocumentsDownloader key={file.url} image={file.url} name={file.name} comment={file.comment} project_id={projectId} containerStyles={FILE_CONTAINER_STYLE} uploadedAt={file.uploaded_time ?? dateTime} />
                ))}
            </div>
            {normalizedComment.length > 0 && (
                <>
                    <hr />
                    <strong>Comment:</strong> <i>{normalizedComment}</i>
                </>
            )}
        </SmallSection>
    )
}
OrderDocumentSection.propTypes = {
    projectId: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    files: FilesPropType,
    dateTime: PropTypes.string,
    comment: PropTypes.string,
}

const OrderDocuments = ({ projectId, documents = {}, finisherTimelineVisible }) => {
    return (
        <>
            {LEGACY_DOCUMENT_SECTIONS.map(({ key, fallbackKeys, title }) => (
                <OrderDocumentSection
                    key={key}
                    title={getSectionTitle(title, { finisherTimelineVisible })}
                    projectId={projectId}
                    files={getSectionFiles(documents, key, fallbackKeys)}
                />
            ))}
        </>
    )
}

OrderDocuments.propTypes = {
    projectId: PropTypes.number.isRequired,
    finisherTimelineVisible: PropTypes.bool,
    documents: PropTypes.shape({
        supplier_files: FilesPropType,
        vendor_template: FilesPropType,
        order_template_to_studio: FilesPropType,
        studio_template: FilesPropType,
        order_template_to_client: FilesPropType,
        make_artwork_send_to_studio: FilesPropType,
        make_artwork_from_studio: FilesPropType,
        make_artwork_send_to_client: FilesPropType,
        client_artwork: FilesPropType,
        order_artwork_to_studio: FilesPropType,
        studio_artwork: FilesPropType,
        order_artwork_to_vendor: FilesPropType,
        vendor_proof: FilesPropType,
        order_proof_to_studio: FilesPropType,
        studio_proof: FilesPropType,
        order_proof_to_client: FilesPropType,
        vendor_purchase_order: FilesPropType,
        finisher_purchase_order: FilesPropType,
        vendor_delivery_notes: FilesPropType,
        finisher_delivery_notes: FilesPropType,
        supplier_delivery_notes: FilesPropType,
        supplier_delivey_notes: FilesPropType,
    })
}

const OrderDocumentsNew = ({ projectId, documents = [] }) => {
    return (Array.isArray(documents) ? documents : [])
        .filter(({ type }) => !!ORDER_DOCUMENT_TYPES[type])
        .map(({ id, documents: files, type, comments, updatedAt }) => (
            <OrderDocumentSection
                key={id}
                projectId={projectId}
                title={ORDER_DOCUMENT_TYPES[type]}
                files={files}
                dateTime={updatedAt}
                comment={comments}
            />
        ))
}
OrderDocumentsNew.propTypes = {
    projectId: PropTypes.number.isRequired,
    finisherTimelineVisible: PropTypes.bool,
    documents: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        documents: FilesPropType.isRequired,
        updatedAt: PropTypes.string,
        type: PropTypes.string,
    })),
}

export { OrderDocuments, OrderDocumentsNew };
